/**
 * `.dims` — les DEUX lectures d'un même savoir, côte à côte, séparées par un
 * filet, et **sans troisième colonne** (maquette v5 `geometrie.html`, écrans
 * G4 et G7 ; `SPEC-REPRESENTATION-VISUELLE.md` §5.6).
 *
 * La grille `1fr 1px 1fr` n'est pas un choix esthétique : c'est l'invariant 2
 * rendu structurel. Il n'existe nulle part une valeur qui résume les deux
 * dimensions — ni moyenne, ni pourcentage, ni « niveau global », ni minimum
 * ordinal (« le savoir prend le palier le plus bas des deux »), qui serait
 * arithmétiquement innocent et pédagogiquement faux : il masquerait la force
 * en calcul de l'élève et cacherait la seule chose à travailler.
 *
 * Ce composant n'expose aucune propriété où écrire un tel total. Un savoir qui
 * ne porte pas de figure affiche UNE colonne, sous le libellé « Une seule
 * lecture » — la deuxième n'apparaît que là où elle a un sens, et l'écran le
 * dit.
 */
import {
  LIBELLES_DIMENSION,
  LIBELLE_DIMENSION_UNIQUE,
  type DimensionSavoir,
  type NiveauMaitrise,
} from '@silex/shared';

import { PalierMaitrise } from './PalierMaitrise';

export interface DimsSavoirProps {
  /**
   * Un palier par dimension observée. Une dimension absente est rendue
   * `non_evalue` sous son nom — jamais un vide, jamais un tiret : « pas encore
   * évalué » est une information, l'absence n'en est pas une.
   */
  readonly paliers: Partial<Readonly<Record<DimensionSavoir, NiveauMaitrise>>>;
  /**
   * Dérivé côté serveur de `SavoirPDA.categorie` par
   * `savoirPorteLaLectureDeFigure` — jamais deviné ici.
   */
  readonly porteLaLectureDeFigure: boolean;
}

export function DimsSavoir({ paliers, porteLaLectureDeFigure }: DimsSavoirProps) {
  if (!porteLaLectureDeFigure) {
    return (
      /* Une seule colonne, mais le MÊME traitement : sans la classe `dims`,
         le palier retombait en ligne et la carte se lisait comme deux
         composants différents pour la même mesure (vu en capture). */
      <div className="dims dims--seule">
        <div>
          <span className="dims-lbl">{LIBELLE_DIMENSION_UNIQUE}</span>
          <PalierMaitrise niveau={paliers.calcul ?? 'non_evalue'} />
        </div>
      </div>
    );
  }

  return (
    <div className="dims">
      {/* « Lire la figure » à gauche du filet, « Calculer » à droite — l'ordre
          de la maquette, celui du contrat (`dimensionsDuSavoir`). */}
      <div>
        <span className="dims-lbl">{LIBELLES_DIMENSION.lecture_figure}</span>
        <PalierMaitrise niveau={paliers.lecture_figure ?? 'non_evalue'} />
      </div>
      <div className="dims-sep" aria-hidden="true" />
      <div>
        <span className="dims-lbl">{LIBELLES_DIMENSION.calcul}</span>
        <PalierMaitrise niveau={paliers.calcul ?? 'non_evalue'} />
      </div>
    </div>
  );
}
